// DTOs for cross-context calls into materials, costing, inventory (ledger) and purchasing
// [04 §11, BR-COST-015; 05 §3, §4, §6, §9, §10, §17; MB D7].
import { z } from 'zod';

import { businessDateSchema, idSchema, uuidV7Schema } from '../kernel/index.ts';
import { moneySchema, unitRateSchema, uomSchema } from '../units/index.ts';
import { ETA_MILESTONE, ETA_SOURCE, MOVEMENT_TYPE, type MovementType } from '../enums/index.ts';
import { documentRefSchema, gateCheckSchema } from './common.ts';
import { qtyDtoSchema, utcTimestampSchema } from './scalars.ts';

// ---- materials [05 §3 priceAt, §4 lots, BR-INV-002 / G6] ----

export const materialKindValues = ['fabric', 'trim'] as const;

export const priceAtQuerySchema = z.strictObject({
  materialKind: z.enum(materialKindValues),
  materialId: idSchema,
  materialColourId: idSchema.nullable(),
  supplierId: idSchema.nullable(),
  date: businessDateSchema,
  uom: uomSchema.nullable(),
});

export const materialPriceDtoSchema = z.strictObject({
  priceId: idSchema,
  unitPrice: unitRateSchema,
  uom: uomSchema,
  effectiveFrom: businessDateSchema,
  supplierId: idSchema.nullable(),
});

/**
 * Goods-receipt lines become lots (dye lot) and rolls; called by purchasing on receipt post. CR-W2a-39 R-CORE-3: the
 * PO (`poId`, BR-LOT-002), "add to existing lot" per line (`addToLotId`, BR-LOT-001) and `rollNo` null = next integer
 * in the lot (BR-LOT-003).
 */
export const createLotsFromReceiptInputSchema = z.strictObject({
  goodsReceiptId: idSchema,
  supplierId: idSchema,
  receivedOn: businessDateSchema,
  poId: idSchema.nullable().optional(),
  lines: z
    .array(
      z.strictObject({
        receiptLineId: idSchema,
        materialColourId: idSchema,
        dyeLot: z.string().min(1),
        unitCost: unitRateSchema,
        addToLotId: idSchema.nullable().optional(),
        rolls: z
          .array(
            z.strictObject({
              rollNo: z.string().min(1).nullable(),
              qty: qtyDtoSchema,
              binId: idSchema.nullable(),
            }),
          )
          .min(1),
      }),
    )
    .min(1),
});

export const createdLotsDtoSchema = z.strictObject({
  lots: z.array(
    z.strictObject({
      lotId: idSchema,
      receiptLineId: idSchema,
      dyeLot: z.string().min(1),
      rollIds: z.array(idSchema),
    }),
  ),
});

/** Why a lot is excluded from availability (G6, not overridable) [BR-INV-002; BR-DEV-005]. */
export const lotBlockReasonValues = ['held', 'test_pending', 'test_failed', 'rejected'] as const;

export const lotUsabilityDtoSchema = z.strictObject({
  lotId: idSchema,
  usable: z.boolean(),
  reason: z.enum(lotBlockReasonValues).nullable(),
});

// ---- costing [04 §11 G1, BR-COST-015, BR-PST-003] ----

export const styleOrderableQuerySchema = z.strictObject({
  styleId: idSchema,
  date: businessDateSchema,
});

/** G1: an approved cost sheet makes a style orderable (override `orders.override`). */
export const styleOrderableDtoSchema = z.strictObject({
  styleId: idSchema,
  approvedVersionId: idSchema.nullable(),
  gate: gateCheckSchema,
});

/** `GET /api/v1/cost-sheets/approved-prices?styleId=` used by the PO price tolerance check. */
export const approvedMaterialPriceDtoSchema = z.strictObject({
  styleId: idSchema,
  approvedVersionId: idSchema,
  materialKind: z.enum(materialKindValues),
  materialId: idSchema,
  materialColourId: idSchema.nullable(),
  unitPrice: unitRateSchema,
  uom: uomSchema,
});

/** Approved-estimate unit cost used on FG receipt until the costing close sets actual [BR-PST-003]. */
export const approvedUnitCostQuerySchema = z.strictObject({
  styleId: idSchema,
  colourwayId: idSchema.nullable(),
  date: businessDateSchema,
});

export const approvedUnitCostDtoSchema = z.strictObject({
  styleId: idSchema,
  approvedVersionId: idSchema,
  unitCost: unitRateSchema,
});

// ---- inventory ledger [05 §9 BR-INV-001..010, §17; 03 §19.1; MB D7] ----

/**
 * One signed movement; type from the closed 03 §19.1 list (kernel enum), corrections are reversals. `binId` (a bin of
 * the same warehouse) and `transferGroup` (one uuid shared by the out/in legs of a transfer or bin-to-bin move,
 * AC-LOT-02, BR-LOT-006) are optional (CR-W2a-39 R-LED-2).
 */
export const movementInputSchema = z.strictObject({
  movementType: MOVEMENT_TYPE.schema,
  itemId: idSchema,
  warehouseId: idSchema,
  lotId: idSchema.nullable(),
  rollId: idSchema.nullable(),
  qty: qtyDtoSchema,
  enteredQty: qtyDtoSchema.nullable(),
  unitCost: unitRateSchema.nullable(),
  reasonCode: z.string().min(1).nullable(),
  reference: documentRefSchema,
  businessDate: businessDateSchema,
  binId: idSchema.nullable().optional(),
  transferGroup: uuidV7Schema.nullable().optional(),
  /**
   * CR-W2b-12 (CR-W2b-1 R-LED-5): the inventory category the quantity is booked in (an active category of the item's
   * subject); default = the subject's default category. Category changes post `category_out` / `category_in` pairs.
   */
  categoryId: idSchema.nullable().optional(),
  /**
   * CR-W2b-12 (R-LED-5, BR-INV-008): a recorded override (gate NEGATIVE_AVAILABLE, `inventory.override`, on the
   * reference document) that lets a non-lot-tracked item go below zero; lot / roll tracked stock never may.
   */
  negativeOverrideId: idSchema.nullable().optional(),
  /**
   * CR-BG2d (R-LED-SHP-1): required on `shipment_reversal` (and only there): the posted `shipment` movement of the same
   * item × warehouse × lot × roll it puts back, at that movement's unit cost; all reversals of one movement never exceed
   * its quantity. A movement that consumed a reservation revives it by the reversed quantity. CR-BG2e (R-CORE-FGR-1,
   * R-LED-CDN-1): the same for `fg_receipt_reversal` (of an `fg_receipt`, same document) and `supplier_return_reversal`
   * (of a `supplier_return`, booked on another document of the same type: the opposite note).
   */
  reversalOfId: idSchema.nullable().optional(),
  /**
   * CR-BG2e (R-LED-CLS-1, BR-LC-003): required on `revaluation` (and only there): the signed value change in CAD minor
   * units of a value-only movement (qty 0) on stock of the scope; never takes the scope's value below zero.
   */
  valueMinor: z.int().nullable().optional(),
});

export const postedMovementDtoSchema = z.strictObject({
  movementId: idSchema,
  movementType: MOVEMENT_TYPE.schema,
  itemId: idSchema,
  warehouseId: idSchema,
  lotId: idSchema.nullable(),
  rollId: idSchema.nullable(),
  qty: qtyDtoSchema,
  unitCost: unitRateSchema,
  value: moneySchema,
  postedAt: utcTimestampSchema,
});

/**
 * BR-INV-003: inside the item × warehouse lock; lot required for lot-tracked items. `overrideId` names a recorded G5
 * override (`core.override_event`, gate LOT_MIX, permission `cutting.override`) on the demand document; with it a
 * second dye lot on the same demand line is accepted (AC-LOT-05, T18; CR-W2a-39 R-LED-3).
 */
export const reserveInputSchema = z.strictObject({
  demand: documentRefSchema,
  demandLineId: idSchema.nullable(),
  itemId: idSchema,
  warehouseId: idSchema,
  qty: qtyDtoSchema,
  lotId: idSchema.nullable(),
  rollId: idSchema.nullable(),
  overrideId: idSchema.nullable().optional(),
});

export const reservationDtoSchema = z.strictObject({
  reservationId: idSchema,
  demand: documentRefSchema,
  demandLineId: idSchema.nullable(),
  itemId: idSchema,
  warehouseId: idSchema,
  lotId: idSchema.nullable(),
  rollId: idSchema.nullable(),
  qty: qtyDtoSchema,
  consumedQty: qtyDtoSchema,
});

/** BR-INV-006: qty null releases the whole open remainder. */
export const releaseReservationInputSchema = z.strictObject({
  reservationId: idSchema,
  qty: qtyDtoSchema.nullable(),
  reason: z.string().min(1),
});

/**
 * CR-BG2d (R-LED-SHP-1): the movement types an issue of reserved stock may write (default `cut_issue`). CR-BG2f
 * (R-CORE-D2C-4): `d2c_sale` issues a web order's own reservations (demand type `d2c_order`).
 */
export const RESERVED_ISSUE_MOVEMENT_TYPES = ['cut_issue', 'shipment', 'd2c_sale'] as const satisfies readonly MovementType[];

/**
 * BR-INV-007: writes `cut_issue` of the reserved lot/roll and raises consumed qty. CR-BG2d (R-LED-SHP-1): with
 * `movementType: 'shipment'` (a reservation of demand type `shipment` only) it is the dispatch issue (BR-SHP-012); the
 * movement and the reservation's consumed qty / status commit together. CR-BG2f (R-CORE-D2C-4): `d2c_sale` likewise
 * issues a reservation of demand type `d2c_order` under its own order reference (BR-D2C-014 ship).
 */
export const issueReservedInputSchema = z.strictObject({
  reservationId: idSchema,
  qty: qtyDtoSchema,
  rollId: idSchema.nullable(),
  reference: documentRefSchema,
  businessDate: businessDateSchema,
  movementType: z.enum(RESERVED_ISSUE_MOVEMENT_TYPES).optional(),
});

export const returnToStockInputSchema = z.strictObject({
  itemId: idSchema,
  warehouseId: idSchema,
  lotId: idSchema.nullable(),
  rollId: idSchema.nullable(),
  qty: qtyDtoSchema,
  reference: documentRefSchema,
  reasonCode: z.string().min(1).nullable(),
  businessDate: businessDateSchema,
});

/** `availableAt(item, wh, lot?, roll?)`; asOf null = now. */
export const availabilityQuerySchema = z.strictObject({
  itemId: idSchema,
  warehouseId: idSchema,
  lotId: idSchema.nullable(),
  rollId: idSchema.nullable(),
  asOf: utcTimestampSchema.nullable(),
});

/** available = on hand − reserved − held [MB D7]. */
export const availabilityDtoSchema = z.strictObject({
  itemId: idSchema,
  warehouseId: idSchema,
  lotId: idSchema.nullable(),
  rollId: idSchema.nullable(),
  onHand: qtyDtoSchema,
  reserved: qtyDtoSchema,
  held: qtyDtoSchema,
  available: qtyDtoSchema,
});

/** BR-INV-004 lot suggestion (FIFO single covering lot, else largest with shortfall). */
export const lotSuggestionQuerySchema = z.strictObject({
  itemId: idSchema,
  warehouseId: idSchema,
  qty: qtyDtoSchema,
});

export const lotSuggestionDtoSchema = z.strictObject({
  lotId: idSchema.nullable(),
  coversAll: z.boolean(),
  shortfall: qtyDtoSchema,
});

// ---- purchasing [05 §6, §10 BR-PLN-012/013] ----

export const poSuggestionInputSchema = z.strictObject({
  suggestionId: idSchema,
  supplierId: idSchema,
  itemId: idSchema,
  qty: qtyDtoSchema,
  unitPrice: unitRateSchema,
  requiredBy: businessDateSchema,
});

export const createdDraftPoDtoSchema = z.strictObject({
  purchaseOrderId: idSchema,
  number: z.string().min(1).nullable(),
  supplierId: idSchema,
  lines: z.array(z.strictObject({ suggestionId: idSchema, purchaseOrderLineId: idSchema })),
});

export const etaFeedImportInputSchema = z.strictObject({
  source: ETA_SOURCE.schema,
  forwarderId: idSchema.nullable(),
  idempotencyKey: uuidV7Schema,
  rows: z
    .array(
      z.strictObject({
        reference: z.string().min(1),
        milestone: ETA_MILESTONE.schema,
        date: businessDateSchema,
        note: z.string().nullable(),
      }),
    )
    .min(1),
});

export const etaImportResultDtoSchema = z.strictObject({
  importId: idSchema,
  matched: z.int().nonnegative(),
  exceptions: z.int().nonnegative(),
});

export type PriceAtQuery = z.output<typeof priceAtQuerySchema>;
export type MaterialPriceDto = z.output<typeof materialPriceDtoSchema>;
export type CreateLotsFromReceiptInput = z.output<typeof createLotsFromReceiptInputSchema>;
export type CreatedLotsDto = z.output<typeof createdLotsDtoSchema>;
export type LotUsabilityDto = z.output<typeof lotUsabilityDtoSchema>;
export type StyleOrderableQuery = z.output<typeof styleOrderableQuerySchema>;
export type StyleOrderableDto = z.output<typeof styleOrderableDtoSchema>;
export type ApprovedMaterialPriceDto = z.output<typeof approvedMaterialPriceDtoSchema>;
export type ApprovedUnitCostQuery = z.output<typeof approvedUnitCostQuerySchema>;
export type ApprovedUnitCostDto = z.output<typeof approvedUnitCostDtoSchema>;
export type MovementInput = z.output<typeof movementInputSchema>;
export type PostedMovementDto = z.output<typeof postedMovementDtoSchema>;
export type ReserveInput = z.output<typeof reserveInputSchema>;
export type ReservationDto = z.output<typeof reservationDtoSchema>;
export type ReleaseReservationInput = z.output<typeof releaseReservationInputSchema>;
export type IssueReservedInput = z.output<typeof issueReservedInputSchema>;
export type ReturnToStockInput = z.output<typeof returnToStockInputSchema>;
export type AvailabilityQuery = z.output<typeof availabilityQuerySchema>;
export type AvailabilityDto = z.output<typeof availabilityDtoSchema>;
export type LotSuggestionQuery = z.output<typeof lotSuggestionQuerySchema>;
export type LotSuggestionDto = z.output<typeof lotSuggestionDtoSchema>;
export type PoSuggestionInput = z.output<typeof poSuggestionInputSchema>;
export type CreatedDraftPoDto = z.output<typeof createdDraftPoDtoSchema>;
export type EtaFeedImportInput = z.output<typeof etaFeedImportInputSchema>;
export type EtaImportResultDto = z.output<typeof etaImportResultDtoSchema>;
